import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import ProcessingCard from 'components/ProcessingCard';
import StandardSubmissionWarning from 'components/StandardSubmissionWarning/StandardSubmissionWarning';

import { apiValidations } from 'api';
import { useAppDispatch } from 'hooks';
import { setLineOfBusiness } from 'reducers/specialtyFormReducer';

import SpecialtyFormView from 'views/specialty_form/SpecialtyFormView';
import {
  RUNNER_VALIDATE_AUTO_QUOTE,
  RUNNER_VALIDATE_HOME_QUOTE,
} from 'variables/constants';

interface SpecialtyPolicyProps {
  policyUuid?: string;
  submissionId?: number | null;
  onProcessingComplete?: () => void;
  runner?: number;
  isLOA?: boolean;
}

const SpecialtyPolicyView = ({
  policyUuid,
  submissionId,
  onProcessingComplete,
  runner,
  isLOA = false,
}: SpecialtyPolicyProps) => {
  const { uuid: urlUuid } = useParams();
  const uuid = policyUuid || urlUuid;
  const dispatch = useAppDispatch();

  // Set line of business based on runner
  useEffect(() => {
    if (runner === RUNNER_VALIDATE_AUTO_QUOTE) {
      dispatch(setLineOfBusiness('Auto'));
    } else if (runner === RUNNER_VALIDATE_HOME_QUOTE) {
      dispatch(setLineOfBusiness('Home'));
    }
  }, [runner, dispatch]);

  // Skip validation query for LOA policies - they don't have a quote document
  const { data, isLoading, error } = useQuery({
    queryKey: ['specialty-validation', uuid, isLOA],
    queryFn: async () => {
      if (isLOA) {
        return { parsable: false, isLOA: true };
      }

      const response = await apiValidations(uuid!, undefined, submissionId);

      if (response && response.status === 202) {
        throw { status: 202, message: 'Processing in progress' };
      }

      if (onProcessingComplete) onProcessingComplete();

      return response;
    },
    // Disable the query for LOA policies
    enabled: !isLOA,
    retry: (failureCount, error: any) => {
      if (error?.status === 202) {
        return true; // Keep retrying on processing status
      }

      if (error?.status === 400) {
        return false; // Don't retry on bad request
      }

      return failureCount < 3;
    },
    retryDelay: (attemptIndex, error: any) => {
      if (error?.status === 202) {
        return 1000; // Check every second when processing
      }

      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
  }) as {
    data: {
      parsable: boolean;
    };
    isLoading: boolean;
    error: any;
  };

  // For LOA policies, skip validation and go directly to the form
  if (isLOA) {
    return <SpecialtyFormView />;
  }

  // Processing state
  if (isLoading || error?.status === 202) {
    return <ProcessingCard title="Processing Speciality Quote Document" />;
  }

  // Success state - Display parsable status
  if (data) {
    if (data.parsable) {
      return <StandardSubmissionWarning />;
    }

    return <SpecialtyFormView />;
  }

  return null;
};

export default SpecialtyPolicyView;
